import React from 'react';
import { motion } from 'framer-motion';
import { Clock, Compass, ListChecks, Volume2 } from 'lucide-react';
const finderFilters = [{
  label: 'Level',
  value: 'Beginner · Intermediate · Advanced'
}, {
  label: 'Focus',
  value: 'Technique · Footwork · Defense · Counters · Combinations · Conditioning · Tactics'
}, {
  label: 'Session type',
  value: 'Individual · Partner · Pads · Bag · Tactical · Conditioning'
}, {
  label: 'Duration',
  value: '30 · 45 · 60 · 75+ minutes'
}, {
  label: 'Training need',
  value: 'Foundation · Correction · Progression · Reaction · Pressure · Assessment'
}, {
  label: 'Context',
  value: 'Individual · Small group · Large group · Limited equipment · No-prep'
}];
const sessionAnatomy = ['Clear objective', 'Recommended athlete level', 'Equipment', 'Structured warm-up', 'Technical preparation', 'Main drills', 'Exact rounds or work/rest', 'Coaching cues', 'Common errors', 'Corrections', 'Regressions', 'Progressions', 'Conditioning or finisher when appropriate', 'Cool-down', 'Success criteria', 'Safety considerations', 'Exit standard where appropriate'];
const supportPillars = [{
  icon: Volume2,
  title: 'Coaching cues & corrections',
  text: 'No long explanations you cannot use while an athlete is standing in front of you. Short, practical coaching cues designed to correct one meaningful cause at a time.'
}, {
  icon: ListChecks,
  title: 'Progressions & regressions',
  text: 'Know when the athlete should repeat, simplify, slow down, reduce range, remove a variable, add movement, add reaction, add decision-making, or progress toward tactical application.'
}];
export function TeachingLibrary() {
  return <section className="py-24 px-4 bg-cream border-y border-silver-light" data-id="element-248" data-mp="3j7vll41apd7w">
      <div className="max-w-6xl mx-auto" data-id="element-249" data-mp="2lqdmr136uztk">
        <div className="text-center mb-16" data-id="element-250" data-mp="2lpc9cpypa18o">
          <p className="text-accent font-semibold tracking-widest uppercase text-xs mb-3" data-id="element-251" data-mp="2qp84s0tc0kcm">
            The core of Boxing Coach Ready
          </p>
          <h2 className="text-4xl md:text-5xl font-display font-extrabold mb-4 text-ink" data-id="element-252" data-mp="2in7hltne6oqo">
            <span className="text-accent" data-id="element-253" data-mp="2elw06beo99tk">150 complete</span> ready-to-coach sessions
          </h2>
          <p className="text-xl text-graphite" data-id="element-254" data-mp="2shhla7hsb9gp">
            Organized by skill, level and coaching objective · No need to build every training
            session from zero
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 mb-12" data-id="element-255" data-mp="1zsskk9kn565h">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          ease: [0.23, 1, 0.32, 1]
        }} className="lg:col-span-3 bg-paper border border-silver-light rounded-2xl p-8" data-id="element-256" data-mp="1vq5xelyx09em">
            <div className="flex items-center gap-3 mb-2" data-id="element-257" data-mp="3l27qxrf5zpf4">
              <Compass className="text-accent" size={22} aria-hidden="true" data-id="element-258" data-mp="3g5r36ybvnd2k" />
              <h3 className="text-2xl font-display font-extrabold text-ink" data-id="element-259" data-mp="11xgg0ry059yn">Session Finder</h3>
            </div>
            <p className="text-graphite mb-6" data-id="element-260" data-mp="16wt23ah1ud06">
              Instead of asking “What should I coach today?”, narrow the material according to what
              your athletes actually need.
            </p>
            <dl className="space-y-3" data-id="element-261" data-mp="xutjidqdz0v4">
              {finderFilters.map(filter => <div key={filter.label} className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4 border-b border-silver-light pb-3 last:border-0 last:pb-0" data-id="element-262" data-mp="1lknv6ryyyjds">
                  <dt className="text-sm font-display font-bold text-ink uppercase tracking-wider sm:w-32 shrink-0" data-id="element-263" data-mp="1bubfbd23nedz">
                    {filter.label}
                  </dt>
                  <dd className="text-sm text-graphite" data-id="element-264" data-mp="24jib44a65pvj">{filter.value}</dd>
                </div>)}
            </dl>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          delay: 0.05,
          ease: [0.23, 1, 0.32, 1]
        }} className="lg:col-span-2 bg-ink rounded-2xl p-8" data-id="element-265" data-mp="29c31evtv9kub">
            <div className="flex items-center gap-3 mb-4" data-id="element-266" data-mp="bqda6umjz1n4">
              <Clock className="text-accent" size={22} aria-hidden="true" data-id="element-267" data-mp="6q7ri3y9gph6" />
              <h3 className="text-xl font-display font-extrabold text-white" data-id="element-268" data-mp="13f6xfcbdhf67">Session durations</h3>
            </div>
            <p className="text-sm text-white/70 mb-5" data-id="element-269" data-mp="zdlg9ert2eq7">
              Useful whether you have a short technical block or a complete training session.
            </p>
            <ul className="grid grid-cols-2 gap-2 text-sm" data-id="element-270" data-mp="po1wm6w9qgua">
              {['30 min', '45 min', '60 min', '75+ min'].map(duration => <li key={duration} className="bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-center font-medium text-white" data-id="element-271" data-mp="1n4heatmuu65w">
                  {duration}
                </li>)}
            </ul>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.3,
        ease: [0.23, 1, 0.32, 1]
      }} className="bg-paper border border-silver-light rounded-2xl p-8 mb-8" data-id="element-272" data-mp="1unqcdqpwuifn">
          <h3 className="text-xl font-display font-extrabold text-ink mb-6" data-id="element-273" data-mp="28lx4src3bq7x">
            Every session includes
          </h3>
          <ul className="grid md:grid-cols-2 gap-x-8 gap-y-3" data-id="element-274" data-mp="2djq8ndpx8851">
            {sessionAnatomy.map(item => <li key={item} className="flex items-start gap-3" data-id="element-275" data-mp="2ievs85vupapy">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-accent shrink-0" aria-hidden="true" data-id="element-276" data-mp="2nb30447t4waj" />
                <span className="text-sm text-graphite" data-id="element-277" data-mp="2wc14inm5r5tn">{item}</span>
              </li>)}
          </ul>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6" data-id="element-278" data-mp="waumzcc71dsq">
          {supportPillars.map((pillar, index) => <motion.div key={pillar.title} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          delay: index * 0.05,
          ease: [0.23, 1, 0.32, 1]
        }} className="bg-cream-light border border-silver-light rounded-2xl p-6 flex flex-col" data-id="element-279" data-mp="sa0xwfn0lsy9" data-mpi={index}>
              <pillar.icon className="text-accent mb-4" size={22} aria-hidden="true" data-id="element-280" data-mp="1ay5yje8dkxsu" />
              <h4 className="font-display font-extrabold text-lg text-ink mb-2" data-id="element-281" data-mp="1oxnq21d22p5w">{pillar.title}</h4>
              <p className="text-sm text-graphite leading-relaxed" data-id="element-282" data-mp="1k0oy9pgv6uav">{pillar.text}</p>
            </motion.div>)}
        </div>
      </div>
    </section>;
}